import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { compararValoresOrdenacao } from '../../../../../shared/helpers/ordenacao-tabela.helper';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ClienteListaPrecoStruct } from '../../../../../shared/services/structs/produto/cliente-lista-preco.struct';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { environment } from '../../../../../../environments/environment';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { PessoaLookupService } from '../../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { ListPessoaIdsRequest } from '../../../../../shared/services/requests/pessoa/list-pessoa-ids-request';
import { TipoContatoEnum } from '../../../../../shared/enums/pessoa/tipo-contato.enum';

@Component({
  selector: 'app-lista-preco-cadastro-clientes',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    SelectPesquisavelComponent,
    AlertCardComponent,
  ],
  templateUrl: './lista-preco-cadastro-clientes.component.html',
  styleUrl: './lista-preco-cadastro-clientes.component.css',
})
export class ListaPrecoCadastroClientesComponent implements OnInit {
  @Input() clientes: ClienteListaPrecoStruct[] = [];
  @Input() isTodosClientes: boolean = true;
  @Input() isPadrao: boolean = false;

  @Output() clientesChange = new EventEmitter<ClienteListaPrecoStruct[]>();

  public urlCliente =
    `${environment.urlApiPessoa}Lookup/listPessoaSelect?idTipoContato=` + TipoContatoEnum.Cliente;
  public displayedColumns: string[] = ['nome', 'cpfCnpj', 'acoes'];

  public showAddRow: boolean = false;
  public isCarregandoClientes: boolean = false;
  public novoClienteControl = new FormControl(null);
  public clienteSelecionado: any = null;

  // Filtro
  public filtroTexto: string = '';

  // Ordenação
  public sortColumn: string = '';
  public sortDirection: 'asc' | 'desc' | '' = '';

  // Paginação "Ver mais"
  private readonly PAGE_SIZE = 30;
  public displayLimit: number = this.PAGE_SIZE;

  get clientesFiltrados(): ClienteListaPrecoStruct[] {
    let lista = this.clientes;

    // Filtro por texto
    if (this.filtroTexto) {
      const termo = this.filtroTexto.toLowerCase();
      lista = lista.filter(
        c => c.nome?.toLowerCase().includes(termo) || c.cpfCnpj?.toLowerCase().includes(termo)
      );
    }

    // Ordenação
    if (this.sortColumn && this.sortDirection) {
      const dir = this.sortDirection === 'asc' ? 1 : -1;
      lista = [...lista].sort((a: any, b: any) =>
        compararValoresOrdenacao(a[this.sortColumn] ?? '', b[this.sortColumn] ?? '') * dir
      );
    }

    return lista;
  }

  get clientesVisiveis(): ClienteListaPrecoStruct[] {
    return this.clientesFiltrados.slice(0, this.displayLimit);
  }

  get hasMore(): boolean {
    return this.displayLimit < this.clientesFiltrados.length;
  }

  verMais(): void {
    this.displayLimit += this.PAGE_SIZE;
  }

  onSort(sort: Sort): void {
    this.sortColumn = sort.active;
    this.sortDirection = sort.direction;
    this.displayLimit = this.PAGE_SIZE;
  }

  onFilter(): void {
    this.displayLimit = this.PAGE_SIZE;
  }

  // Formata CPF (11 dígitos) e CNPJ (12 alfanuméricos + 2 dígitos verificadores) na exibição.
  // Documento estrangeiro que não bate com nenhum dos dois formatos é exibido como veio da API.
  formatDocumento(value: string | null | undefined): string {
    if (!value) return '-';
    const raw = value.toString().replace(/[.\-\/\s]/g, '').toUpperCase();
    if (/^\d{11}$/.test(raw)) {
      return raw.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }
    if (/^[A-Z0-9]{12}\d{2}$/.test(raw)) {
      return raw.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, '$1.$2.$3/$4-$5');
    }
    return value;
  }

  constructor(
    private alertService: AlertService,
    private pessoaLookupService: PessoaLookupService
  ) {}

  ngOnInit(): void {
    if (this.clientes.length > 0) {
      this.carregarDadosClientes();
    }
  }

  private carregarDadosClientes(): void {
    const ids = this.clientes.map(c => c.idCliente);
    if (ids.length === 0) return;

    this.isCarregandoClientes = true;

    const request = new ListPessoaIdsRequest();
    request.listIdPessoa = ids;

    this.pessoaLookupService.getListPessoaIds(request).subscribe({
      next: res => {
        this.isCarregandoClientes = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }

        this.clientes = this.clientes.map(c => {
          const pessoa = res.listaPessoa?.find(p => p.idPessoa === c.idCliente);
          if (pessoa) {
            return {
              ...c,
              nome: pessoa.nome,
              cpfCnpj: pessoa.cpf || pessoa.cnpj || null,
            };
          }
          return c;
        });
      },
      error: () => {
        this.isCarregandoClientes = false;
        this.alertService.show('Erro', 'Erro ao carregar dados dos clientes.', AlertType.error);
      },
    });
  }

  get isDisabled(): boolean {
    return this.isTodosClientes || this.isPadrao;
  }

  adicionarLinha(): void {
    this.showAddRow = true;
    this.novoClienteControl.setValue(null);
    this.clienteSelecionado = null;
  }

  onClienteSelecionado(item: any): void {
    this.clienteSelecionado = item;
  }

  confirmarAdicao(): void {
    if (!this.clienteSelecionado) {
      this.alertService.show('Atenção', 'Selecione um cliente.', AlertType.warning);
      return;
    }

    // Verifica se já existe
    const jaExiste = this.clientes.some(c => c.idCliente === this.clienteSelecionado.id);
    if (jaExiste) {
      this.alertService.show('Atenção', 'Este cliente já está na lista.', AlertType.warning);
      return;
    }

    const idNovoCliente = this.clienteSelecionado.id;
    const novo: ClienteListaPrecoStruct = {
      idCliente: idNovoCliente,
      nome: this.clienteSelecionado.descricao ?? this.clienteSelecionado.nome,
      cpfCnpj: null,
    };

    this.clientesChange.emit([...this.clientes, novo]);
    this.cancelarAdicao();

    // O lookup usado no autocomplete (listPessoaSelect) só retorna id/descrição, sem documento —
    // busca o CPF/CNPJ real do cliente recém-adicionado com o mesmo serviço usado para os já existentes.
    const request = new ListPessoaIdsRequest();
    request.listIdPessoa = [idNovoCliente];
    this.pessoaLookupService.getListPessoaIds(request).subscribe({
      next: res => {
        if (res.isError) return;
        const pessoa = res.listaPessoa?.find(p => p.idPessoa === idNovoCliente);
        if (!pessoa) return;
        this.clientes = this.clientes.map(c =>
          c.idCliente === idNovoCliente ? { ...c, cpfCnpj: pessoa.cpf || pessoa.cnpj || null } : c
        );
        this.clientesChange.emit(this.clientes);
      },
    });
  }

  cancelarAdicao(): void {
    this.showAddRow = false;
    this.novoClienteControl.setValue(null);
    this.clienteSelecionado = null;
  }

  removerCliente(index: number): void {
    const nova = this.clientes.filter((_, i) => i !== index);
    this.clientesChange.emit(nova);
  }
}
